import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import MfaChallengePanel from '../components/Mfa/MfaChallengePanel';
import { isMfaChallenge } from '../services/mfaService';
import { setAccessToken } from '../utils/authToken';
import { captureCsrfFromAuthPayload } from '../utils/csrf';
import { apiUrl } from '../utils/apiBaseUrl';
import { getPostLoginDestination } from './Login';

type MagicLinkPurpose = 'login' | 'set_password';

const MagicLink: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const { completeMagicLinkAuth } = useAuth();

  const [purpose, setPurpose] = useState<MagicLinkPurpose | null>(null);
  const [emailHint, setEmailHint] = useState<string | null>(null);
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isValidating, setIsValidating] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [mfaChallenge, setMfaChallenge] = useState<{ mfaToken: string; setupRequired: boolean } | null>(null);

  useEffect(() => {
    if (!token) {
      setError('Invalid link');
      setIsValidating(false);
      return;
    }
    validateToken();
  }, [token]);

  const validateToken = async () => {
    try {
      const response = await fetch(apiUrl(`/v1/auth/magic-link/validate/${token}`));
      const data = await response.json();

      if (!response.ok || !data.valid) {
        setError(data.message || 'Invalid or expired link');
        setIsValidating(false);
        return;
      }

      setPurpose(data.purpose);
      setEmailHint(data.email_hint ?? null);
      setIsValidating(false);

      if (data.purpose === 'login') {
        await consumeLogin();
      }
    } catch {
      setError('Failed to validate link');
      setIsValidating(false);
    }
  };

  const consumeLogin = async () => {
    if (!token) return;
    setIsSubmitting(true);
    setError(null);

    try {
      const response = await fetch(apiUrl('/v1/auth/magic-link/login'), {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token }),
      });

      if (!response.ok) {
        const data = await response.json();
        setError(data.message || data.detail || 'Sign-in failed');
        return;
      }

      const data = await response.json();
      if (isMfaChallenge(data)) {
        setMfaChallenge({
          mfaToken: data.mfa_token,
          setupRequired: data.setup_required,
        });
        return;
      }

      captureCsrfFromAuthPayload(data);
      const user = await completeMagicLinkAuth(data);
      navigate(getPostLoginDestination(user), { replace: true });
    } catch {
      setError('Network error. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!token) return;

    if (password.length < 8) {
      setError('Password must be at least 8 characters long');
      return;
    }
    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      const response = await fetch(apiUrl('/v1/auth/magic-link/set-password'), {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, new_password: password }),
      });

      if (!response.ok) {
        const data = await response.json();
        setError(data.message || data.detail || 'Failed to set password');
        return;
      }

      const data = await response.json();
      if (isMfaChallenge(data)) {
        setMfaChallenge({
          mfaToken: data.mfa_token,
          setupRequired: data.setup_required,
        });
        return;
      }

      captureCsrfFromAuthPayload(data);
      const user = await completeMagicLinkAuth(data);
      navigate(getPostLoginDestination(user), { replace: true });
    } catch {
      setError('Network error. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (mfaChallenge) {
    return (
      <div className="flex flex-col justify-center py-12 min-h-screen bg-gray-50 sm:px-6 lg:px-8">
        <div className="sm:mx-auto sm:w-full sm:max-w-md">
          <div className="px-4 py-8 bg-white shadow sm:rounded-lg sm:px-10">
            <MfaChallengePanel
              mfaToken={mfaChallenge.mfaToken}
              setupRequired={mfaChallenge.setupRequired}
              onError={setError}
              onComplete={async (tokens) => {
                setAccessToken(tokens.access_token, tokens.expires_in);
                const user = await completeMagicLinkAuth(tokens);
                navigate(getPostLoginDestination(user), { replace: true });
              }}
            />
            {error && <p className="mt-4 text-sm text-red-700">{error}</p>}
          </div>
        </div>
      </div>
    );
  }

  if (isValidating || (purpose === 'login' && isSubmitting)) {
    return (
      <div className="flex flex-col justify-center items-center min-h-screen bg-gray-50">
        <div className="w-12 h-12 rounded-full border-b-2 border-blue-600 animate-spin" />
        <p className="mt-4 text-sm text-gray-600">Verifying your secure link…</p>
      </div>
    );
  }

  if (error && purpose !== 'set_password') {
    return (
      <div className="flex flex-col justify-center py-12 min-h-screen bg-gray-50 sm:px-6 lg:px-8">
        <div className="sm:mx-auto sm:w-full sm:max-w-md">
          <div className="px-4 py-8 bg-white shadow sm:rounded-lg sm:px-10 text-center">
            <h2 className="text-xl font-semibold text-gray-900">Link unavailable</h2>
            <p className="mt-3 text-sm text-gray-600">{error}</p>
            <Link
              to="/login"
              className="inline-block mt-6 text-sm font-medium text-blue-600 hover:text-blue-500"
            >
              Go to sign in
            </Link>
          </div>
        </div>
      </div>
    );
  }

  if (purpose === 'set_password') {
    return (
      <div className="flex flex-col justify-center py-12 min-h-screen bg-gray-50 sm:px-6 lg:px-8">
        <div className="sm:mx-auto sm:w-full sm:max-w-md">
          <div className="flex justify-center">
            <img src="/flashailogo.png" alt="Flash AI™" className="w-auto h-20" />
          </div>
          <h2 className="mt-6 text-3xl font-extrabold text-center text-gray-900">
            Set your password
          </h2>
          {emailHint && (
            <p className="mt-2 text-sm text-center text-gray-600">
              Account: {emailHint}
            </p>
          )}
        </div>

        <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
          <div className="px-4 py-8 bg-white shadow sm:rounded-lg sm:px-10">
            <form className="space-y-6" onSubmit={handleSetPassword}>
              {error && (
                <div className="p-4 bg-red-50 rounded-md border border-red-200">
                  <p className="text-sm text-red-800">{error}</p>
                </div>
              )}

              <div>
                <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                  New password
                </label>
                <input
                  id="password"
                  type="password"
                  required
                  minLength={8}
                  className="block px-3 py-2 mt-1 w-full rounded-md border border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                />
              </div>

              <div>
                <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700">
                  Confirm password
                </label>
                <input
                  id="confirmPassword"
                  type="password"
                  required
                  minLength={8}
                  className="block px-3 py-2 mt-1 w-full rounded-md border border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                />
              </div>

              <button
                type="submit"
                disabled={isSubmitting}
                className="flex justify-center px-4 py-2 w-full text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50"
              >
                {isSubmitting ? 'Saving…' : 'Continue to dashboard'}
              </button>
            </form>
          </div>
        </div>
      </div>
    );
  }

  return null;
};

export default MagicLink;
